/**
 * The board's two player colours, and the state that lifts them off the glass.
 *
 * These were a system blue and a system purple, one per side, and they were
 * the last of the cockpit: a dark board with two saturated hues glowing out of
 * it is a heads-up display, and the rest of this design has been walked back
 * from that to frosted glass. The answer was briefly to have no colour at all,
 * one off-white ink on both sides — which fixed the cockpit and produced a
 * board with nothing on it, a grey field with grey numbers, correct and
 * completely lifeless.
 *
 * So the colour comes back, but chosen rather than inherited:
 *
 * - **Teal** and **amber**, not blue and purple. Two hues that no phone OS
 *   uses for "selected" or "link", so neither reads as a control. One cool and
 *   one warm, which is a difference the eye takes in before it has resolved
 *   either as a hue, and which survives being a hundred pixels of a digit's
 *   stroke seen across a room.
 * - They are also safe for the ~8% of men who cannot split red from green:
 *   blue-green against orange stays two things under every common form of
 *   colour blindness, where the green/orange pairing that suggests itself for
 *   a snooker board does not.
 * - Both sit at roughly the same lightness, so neither side looks like it is
 *   winning before a point has been scored.
 *
 * What the colour is *not* spent on is decoration. It lands on the figures
 * that belong to a player — their score, their frames, their break — and
 * everything neutral on the board stays off-white. The at-table contrast is
 * still carried by brightness, not by hue, so the board is readable if the
 * colour is lost entirely to a bad projector or a stream encoder.
 */
export interface Accent {
  /** The colour itself, for text and icons. */
  hex: string;
  /** The same colour as `r,g,b`, for building `rgba()` at a chosen alpha. */
  rgb: string;
}

/** Off-white. The neutral ink, for everything that belongs to nobody. */
export const INK: Accent = { hex: '#f5f5f7', rgb: '245,245,247' };

/** The left seat: teal. */
export const TEAL: Accent = { hex: '#40cbe0', rgb: '64,203,224' };

/** The right seat: amber. */
export const AMBER: Accent = { hex: '#ff9f0a', rgb: '255,159,10' };

export const ACCENT: readonly [Accent, Accent] = [TEAL, AMBER] as const;

export function accentOf(player: 0 | 1): Accent {
  return ACCENT[player] ?? ACCENT[0];
}

/**
 * The variables `.hud-frame` reads, filled in for one player.
 *
 * The live panel takes a rim in the player's colour and the faintest wash of
 * it across the glass; the idle one falls back to the hairline every other
 * card on the board wears. The alphas are deliberately small — a tint on
 * frosted glass reads at a fraction of its nominal strength, but a heavy one
 * turns the panel into a flat coloured rectangle, which is the one thing a
 * translucent surface must never look like. The rim carries the hue; the fill
 * only stops the panel from looking switched off.
 */
export function frameVars(rgb: string, active: boolean): Record<string, string> {
  return {
    '--hud-bright': active ? `rgba(${rgb},0.72)` : 'rgba(255,255,255,0.07)',
    '--hud-glow': active ? `rgba(${rgb},0.2)` : 'transparent',
    '--hud-fill': `rgba(${rgb},${active ? 0.11 : 0})`,
    // The player at the table is the nearest object on the board, so its
    // shadow is longer than everything else's and carries a little of the
    // player's colour in the ambient layer — a lit object tints the surface
    // under it. The idle panel keeps the standard lift.
    ...(active
      ? {
          '--hud-lift': `0 2px 4px -2px rgba(0,0,0,0.75), 0 12px 24px -8px rgba(0,0,0,0.78), 0 40px 72px -30px rgba(0,0,0,0.95), 0 70px 130px -60px rgba(${rgb},0.3)`,
        }
      : {}),
  };
}

/** A neutral block: no colour at all, whatever is happening around it. */
export const QUIET_FRAME: Record<string, string> = {
  '--hud-bright': 'rgba(255,255,255,0.07)',
  '--hud-glow': 'transparent',
  '--hud-fill': 'rgba(255,255,255,0)',
};
